import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  RefreshCw, ChevronDown, ChevronRight, Clock, CheckCircle, XCircle,
  AlertTriangle, Building2, ExternalLink, Play, Loader2,
} from 'lucide-react';

const STATUS_MAP = {
  queued: { label: 'Queued', color: 'bg-slate-100 text-slate-700', icon: Clock },
  running: { label: 'Running', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  completed_with_errors: { label: 'Partial', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const TYPE_MAP = {
  ubm_sync: { label: 'UBM Sync', short: 'UBM' },
  payment_sync: { label: 'Payment Sync', short: 'Payment' },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.queued;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-none ${s.color}`} data-testid={`status-${status}`}>
      <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {s.label}
    </span>
  );
}

function TimeAgo({ date }) {
  if (!date) return <span className="text-muted-foreground">-</span>;
  const d = new Date(date);
  const diff = (Date.now() - d.getTime()) / 1000;
  let text;
  if (diff < 60) text = 'just now';
  else if (diff < 3600) text = `${Math.floor(diff / 60)}m ago`;
  else if (diff < 86400) text = `${Math.floor(diff / 3600)}h ago`;
  else text = d.toLocaleDateString();
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-xs text-muted-foreground cursor-default">{text}</span>
        </TooltipTrigger>
        <TooltipContent className="rounded-none text-xs">{d.toLocaleString()}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function Duration({ ms }) {
  if (!ms) return null;
  const s = ms / 1000;
  return <span className="text-xs text-muted-foreground">{s < 60 ? `${s.toFixed(1)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`}</span>;
}

function CustomerAccordion({ customer, onSync, syncingId }) {
  const [expanded, setExpanded] = useState(false);
  const lastSync = customer.last_sync;
  const lastStatus = customer.last_status;

  return (
    <div className="border border-border" data-testid={`customer-accordion-${customer.account_id}`}>
      <div
        className="w-full flex items-center justify-between p-3 hover:bg-muted/30 transition-colors text-left cursor-pointer"
        onClick={() => setExpanded(!expanded)}
        data-testid={`toggle-${customer.account_id}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {expanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium text-sm truncate">{customer.customer_name}</span>
          <StatusBadge status={lastStatus} />
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs text-muted-foreground">{customer.total_jobs} sync{customer.total_jobs !== 1 ? 's' : ''}</span>
          <span className="text-xs text-muted-foreground">Last: <TimeAgo date={lastSync} /></span>
          <div className="flex gap-1">
            <Button
              variant="outline" size="sm" className="rounded-none h-7 text-xs px-2"
              onClick={(e) => { e.stopPropagation(); onSync(customer.account_id, 'ubm_sync'); }}
              disabled={syncingId === customer.account_id}
              data-testid={`sync-ubm-${customer.account_id}`}
            >
              {syncingId === customer.account_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
              <span className="ml-1">UBM</span>
            </Button>
            <Button
              variant="outline" size="sm" className="rounded-none h-7 text-xs px-2"
              onClick={(e) => { e.stopPropagation(); onSync(customer.account_id, 'payment_sync'); }}
              disabled={syncingId === customer.account_id}
              data-testid={`sync-pmt-${customer.account_id}`}
            >
              {syncingId === customer.account_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
              <span className="ml-1">Payment</span>
            </Button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border bg-muted/10">
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left p-2 pl-10 font-medium text-xs uppercase tracking-wide">Date</th>
                <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Type</th>
                <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Status</th>
                <th className="text-right p-2 font-medium text-xs uppercase tracking-wide">Duration</th>
                <th className="text-right p-2 font-medium text-xs uppercase tracking-wide">Errors</th>
                <th className="text-center p-2 font-medium text-xs uppercase tracking-wide">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(customer.recent_jobs || []).map((job, i) => (
                <tr key={job.job_id + i} className="hover:bg-muted/20">
                  <td className="p-2 pl-10 text-xs">
                    {job.started_at ? new Date(job.started_at).toLocaleString() : '-'}
                  </td>
                  <td className="p-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded-none ${job.type === 'ubm_sync' ? 'bg-indigo-100 text-indigo-700' : 'bg-cyan-100 text-cyan-700'}`}>
                      {TYPE_MAP[job.type]?.short || job.type}
                    </span>
                  </td>
                  <td className="p-2"><StatusBadge status={job.customer_status || job.status} /></td>
                  <td className="p-2 text-right"><Duration ms={job.customer_duration_ms} /></td>
                  <td className="p-2 text-right">
                    {(job.customer_error_count || 0) > 0 ? (
                      <span className="text-red-600 font-medium">{job.customer_error_count}</span>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </td>
                  <td className="p-2 text-center">
                    <a
                      href={`/sync-job/${job.job_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                      data-testid={`view-job-${job.job_id}`}
                    >
                      View <ExternalLink className="h-3 w-3" />
                    </a>
                  </td>
                </tr>
              ))}
              {(!customer.recent_jobs || customer.recent_jobs.length === 0) && (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-xs text-muted-foreground">No sync logs yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function SyncJobsHistory() {
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('by-customer');
  const [syncingId, setSyncingId] = useState(null);
  const pollingRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      if (viewMode === 'by-customer') {
        const { data } = await api.get('/sync-jobs/by-customer');
        setCustomers(data.customers || []);
      } else {
        const params = new URLSearchParams();
        if (typeFilter !== 'all') params.append('job_type', typeFilter);
        if (statusFilter !== 'all') params.append('status', statusFilter);
        const { data } = await api.get(`/sync-jobs/list?${params.toString()}`);
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Failed to load sync jobs', err);
    } finally {
      setLoading(false);
    }
  }, [viewMode, typeFilter, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll when any job is running
  useEffect(() => {
    const hasRunning = jobs.some(j => j.status === 'running' || j.status === 'queued');
    if (hasRunning || syncingId) {
      pollingRef.current = setInterval(fetchData, 4000);
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [jobs, syncingId, fetchData]);

  const handleSync = async (accountId, jobType) => {
    setSyncingId(accountId);
    try {
      // Use full mode for per-customer sync to catch all missing records
      await api.post(`/sync-jobs/trigger?job_type=${jobType}&mode=full&account_id=${accountId}`);
      setTimeout(() => { fetchData(); setSyncingId(null); }, 3000);
    } catch (err) {
      console.error('Failed to trigger sync', err);
      setSyncingId(null);
    }
  };

  const handleSyncAll = async (jobType) => {
    setSyncingId('all');
    try {
      await api.post(`/sync-jobs/trigger?job_type=${jobType}&mode=full`);
      setTimeout(() => { fetchData(); setSyncingId(null); }, 3000);
    } catch (err) {
      console.error('Failed to trigger full sync', err);
      setSyncingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="sync-jobs-history">
      {/* Header & Controls */}
      <div className="border border-border rounded-none bg-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-base">Sync Jobs</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={() => handleSyncAll('ubm_sync')} disabled={!!syncingId} data-testid="sync-all-ubm">
              {syncingId === 'all' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />} Full UBM Sync
            </Button>
            <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={() => handleSyncAll('payment_sync')} disabled={!!syncingId} data-testid="sync-all-payment">
              {syncingId === 'all' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />} Full Payment Sync
            </Button>
            <Button variant="outline" size="sm" className="rounded-none h-8" onClick={fetchData} data-testid="refresh-sync-jobs">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-none border border-border overflow-hidden">
            <button
              className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'by-customer' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted/50'}`}
              onClick={() => setViewMode('by-customer')}
              data-testid="view-by-customer"
            >By Customer</button>
            <button
              className={`px-3 py-1.5 text-xs font-medium border-l border-border ${viewMode === 'all-jobs' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted/50'}`}
              onClick={() => setViewMode('all-jobs')}
              data-testid="view-all-jobs"
            >All Jobs</button>
          </div>
          {viewMode === 'all-jobs' && (
            <>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-44 rounded-none h-8 text-xs" data-testid="filter-type">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="ubm_sync">UBM Sync</SelectItem>
                  <SelectItem value="payment_sync">Payment Sync</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 rounded-none h-8 text-xs" data-testid="filter-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="running">Running</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
        </div>
      </div>

      {/* By Customer View - Accordion */}
      {viewMode === 'by-customer' && (
        <div className="space-y-0" data-testid="customer-accordion-list">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Loading...</div>
          ) : customers.length === 0 ? (
            <div className="border border-border p-8 text-center text-muted-foreground text-sm">
              No sync jobs recorded yet. Click a Sync button above to get started.
            </div>
          ) : (
            customers.map(c => (
              <CustomerAccordion
                key={c.account_id}
                customer={c}
                onSync={handleSync}
                syncingId={syncingId}
              />
            ))
          )}
        </div>
      )}

      {/* All Jobs View - Table */}
      {viewMode === 'all-jobs' && (
        <div className="border border-border rounded-none bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Created</th>
                  <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Type</th>
                  <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Scope</th>
                  <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Status</th>
                  <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">Customers</th>
                  <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Duration</th>
                  <th className="text-center p-3 font-medium text-xs uppercase tracking-wide">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr><td colSpan={7} className="text-center p-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></td></tr>
                ) : jobs.length === 0 ? (
                  <tr><td colSpan={7} className="text-center p-8 text-muted-foreground text-sm">No sync jobs found</td></tr>
                ) : (
                  jobs.map(job => {
                    const dur = job.started_at && job.completed_at
                      ? (new Date(job.completed_at) - new Date(job.started_at))
                      : null;
                    return (
                      <tr key={job.job_id} className="hover:bg-muted/30" data-testid={`job-row-${job.job_id}`}>
                        <td className="p-3 text-xs">{new Date(job.created_at).toLocaleString()}</td>
                        <td className="p-3">
                          <span className={`text-xs px-1.5 py-0.5 rounded-none ${job.type === 'ubm_sync' ? 'bg-indigo-100 text-indigo-700' : 'bg-cyan-100 text-cyan-700'}`}>
                            {TYPE_MAP[job.type]?.label || job.type}
                          </span>
                        </td>
                        <td className="p-3 text-xs">
                          {job.scope === 'customer' ? (
                            <span className="text-muted-foreground">{job.customer_name || 'Single Org'}</span>
                          ) : (
                            <span className="font-medium">Full Sync</span>
                          )}
                        </td>
                        <td className="p-3"><StatusBadge status={job.status} /></td>
                        <td className="p-3 text-right text-xs font-mono">
                          {(job.customers || []).length}
                          {job.progress?.failed > 0 && <span className="text-red-600 ml-1">({job.progress.failed} err)</span>}
                        </td>
                        <td className="p-3"><Duration ms={dur} /></td>
                        <td className="p-3 text-center">
                          <a
                            href={`/sync-job/${job.job_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                          >
                            View <ExternalLink className="h-3 w-3" />
                          </a>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
